import React, {useState, useRef, useImperativeHandle, useLayoutEffect, forwardRef} from 'react'

import {FieldProps, FieldRef, InvalidProps} from './types.js'
import FieldBox from './FieldBox.js'
import {fieldControlClass} from './fieldStyles.js'
import {useFieldId} from './utils.js'
import {field, getFieldCursor, getFieldTextColor, getTrailingPadding} from './styles.js'

export interface TextAreaProps extends FieldProps, InvalidProps {
    id?: string
    inputStyle?: React.CSSProperties
    value?: string
    /** Minimum number of lines, the textarea grows with its content from there */
    rows?: number
    /** The textarea grows up to this many lines (at least rows), then it scrolls */
    maxRows?: number
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
    onBlur?: () => void
    onClick?: (event: React.MouseEvent<HTMLTextAreaElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
}

export interface TextAreaRef extends FieldRef {
    // The textarea element, null while readOnly (the value is then shown in a div)
    getRef: () => HTMLTextAreaElement | null
}

const TextArea = forwardRef<TextAreaRef, TextAreaProps>((props, ref) => {
    const {style, inputStyle, label, info, value, rows = 1, maxRows = field.textAreaMaxRows, readOnly, disabled, isInvalid, onChange, onBlur, onClick, onKeyDown} = props
    const [focused, setFocused] = useState(false)
    const [isInvalidState, setIsInvalidState] = useState(false)

    const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
    // The height the textarea got from the effect below, and the one the user dragged it to (resize handle)
    const autoHeightRef = useRef('')
    const manualHeightRef = useRef(0)
    // The focusable div that shows the value while readOnly
    const readOnlyRef = useRef<HTMLDivElement | null>(null)

    const fieldId = useFieldId(props.id)
    // The readOnly div isn't labelable, it points at the label with aria-labelledby instead
    const labelId = `${fieldId}-label`

    const focus = () => (textAreaRef.current || readOnlyRef.current)?.focus()

    useImperativeHandle(ref, () => ({
        focus,
        blur: () => (textAreaRef.current || readOnlyRef.current)?.blur(),
        getRef: () => textAreaRef.current,
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            focus()
        }
    }), [])

    const showInvalid = isInvalid || isInvalidState

    // Grows with the content from rows lines (and at least the field's height) up to maxRows lines, then it scrolls.
    // A height dragged with the resize handle is kept as a minimum.
    useLayoutEffect(() => {
        const textArea = textAreaRef.current
        if (!textArea) {
            return
        }

        if (textArea.style.height && textArea.style.height !== autoHeightRef.current) {
            manualHeightRef.current = textArea.offsetHeight
        }

        // With height auto it is stretched to the box (field.height) or as high as rows lines, whichever is higher
        textArea.style.height = 'auto'

        const computed = window.getComputedStyle(textArea)
        const lineHeight = parseFloat(computed.lineHeight) || field.textAreaLineHeight
        const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom)
        const maxHeight = Math.ceil(paddingY + lineHeight * Math.max(rows, maxRows))

        const contentHeight = Math.max(textArea.scrollHeight, textArea.offsetHeight)
        const height = Math.max(Math.min(contentHeight, maxHeight), manualHeightRef.current)

        textArea.style.height = `${height}px`
        textArea.style.overflowY = contentHeight > height ? 'auto' : 'hidden'
        autoHeightRef.current = textArea.style.height
    }, [value, rows, maxRows, readOnly, showInvalid])

    const focusedState = focused || value

    // The box (border, background) is drawn by FieldBox, the textarea (or readOnly div) only places the text inside it
    const defaultInputStyle: React.CSSProperties = {
        fontSize: field.fontSize,
        lineHeight: `${field.textAreaLineHeight}px`,
        // More room above the text than Input: the label stays opaque over text that scrolls under it
        paddingTop: 18,
        paddingBottom: 0,
        paddingLeft: field.paddingX,
        // Long lines wrap before the invalid icon at the right of the first line
        paddingRight: getTrailingPadding({invalid: showInvalid}),
        width: '100%',
        // Fills the box (at least field.height) while it has fewer lines, the box grows with it beyond that
        alignSelf: 'stretch',
        overflow: 'hidden',
        outline: 'none',
        border: 'none',
        background: 'transparent',
        color: getFieldTextColor({disabled, readOnly}),
        cursor: getFieldCursor({disabled, readOnly}),
        resize: readOnly ? undefined : 'vertical',
        ...inputStyle
    }

    return (
        <FieldBox
            label={label}
            info={info}
            floated={!!focusedState}
            focused={focused}
            invalid={showInvalid}
            disabled={disabled}
            readOnly={readOnly}
            multiline
            opaqueLabel
            controlId={readOnly ? undefined : fieldId}
            labelId={readOnly ? labelId : undefined}
            onLabelClick={readOnly ? focus : undefined}
            style={style}
        >
            {!readOnly &&
                <textarea
                    className={fieldControlClass}
                    id={fieldId}
                    style={defaultInputStyle}
                    value={value || ''}
                    rows={rows}
                    disabled={disabled}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                        setFocused(false)
                        onBlur?.()
                    }}
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    onChange={(event) => {
                        setIsInvalidState(false)
                        onChange?.(event)
                    }}
                    ref={textAreaRef}
                />
            }

            {readOnly &&
                // Focusable so the value can be selected, without the textarea's resize/typing affordances
                <div
                    // Keeps line breaks and wraps like the textarea
                    style={{...defaultInputStyle, whiteSpace: 'pre-wrap', overflowWrap: 'break-word'}}
                    tabIndex={0}
                    id={fieldId}
                    aria-readonly
                    aria-labelledby={label ? labelId : undefined}
                    ref={readOnlyRef}
                >
                    {value}
                </div>
            }
        </FieldBox>
    )
})

TextArea.displayName = 'TextArea'

export default TextArea
